import React, { useMemo } from 'react';
import { Divider, Flex, TextV2, Box, CloseButton, ButtonV2 } from '@withjoy/joykit';
import { LocationDatesOccupants } from '@apps/accommodations/components/LocationDatesOccupantsSearchBar/LocationDatesOccupantsSearchBar';
import { StaysSearchTerms } from '@apps/stays/types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { ArrowLeft, ChevronBack, Filter, MapMarker } from '@withjoy/joykit/icons';
import { format, parseISO } from 'date-fns';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { getSearchFormInitialValue } from '@apps/accommodations/AccommodationsConsole.utils';
import { pxToRem } from '@withjoy/joykit/theme';
import { closeButtonOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useAccommodationsConsoleConfig } from '@apps/accommodations/AccommodationsConsoleConfigContext';

const Container = ({ children }: { children?: React.ReactNode }) => (
  <Flex
    alignItems="center"
    gap={4}
    padding={4}
    borderBottom="1px solid"
    background="white"
    position="sticky"
    top={0}
    zIndex={1000}
    borderColor="mono3"
  >
    {children}
  </Flex>
);

const AccommodationsConsoleHeader = ({
  handleFormSubmit,
  state,
  onFilterPanelOpen,
  onSearchOpen,
  onClose,
}: {
  handleFormSubmit: (value: StaysSearchTerms) => void;
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
}) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const searchFormInitialValue = useMemo(() => {
    return getSearchFormInitialValue(state);
  }, [state]);

  return (
    <>
      {/* Header Section */}
      <Flex
        alignItems="center"
        gap={4}
        padding={4}
        borderBottom="1px solid"
        background="white"
        position="sticky"
        top={0}
        zIndex={1000}
        borderColor="mono3"
      >
        {isMobileOrTablet ? (
          <MobileHeader
            state={state}
            onFilterPanelOpen={onFilterPanelOpen}
            onSearchOpen={onSearchOpen}
            onClose={onClose}
          />
        ) : (
          <DeskTopHeader
            handleFormSubmit={handleFormSubmit}
            searchFormInitialValue={searchFormInitialValue}
            onClose={onClose}
          />
        )}
      </Flex>
    </>
  );
};

const MobileHeader = ({
  state,
  onFilterPanelOpen,
  onSearchOpen,
  onClose,
}: {
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
}) => {
  /**
   * {
   *   "problem": "When creating Date objects from date strings like '2024-01-15', JavaScript's Date constructor interprets them in the user's local timezone. This can cause dates to shift by one day in certain timezones, especially around midnight UTC transitions.",
   *   "solution": "Using parseISO from date-fns ensures proper ISO string parsing and handles timezone information correctly, preventing date shifting issues.",
   * }
   */
  const checkinDate = format(parseISO(state.searchDates.checkin), 'MMM dd');
  const checkoutDate = format(parseISO(state.searchDates.checkout), 'dd');
  const adults = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.adults, 0) || 0;
  const children = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.childrenAges.length, 0) || 0;
  const {
    topBar: { searchForm },
  } = useAccommodationsConsoleConfig();
  const { t } = useTranslation('accommodationsConsole');
  const headerTrans = t('header');
  const showDatesOrOccupancies = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled;

  return (
    <Flex
      width={'100%'}
      justifyContent={'space-between'}
      alignItems={'center'}
      paddingX={3}
    >
      <ArrowLeft
        size={22}
        cursor={'pointer'}
        onClick={onClose}
      />
      <Flex
        onClick={onSearchOpen}
        cursor={'pointer'}
        alignItems={'center'}
        paddingX={7}
        paddingY={3}
        flexDirection={'column'}
        border={'1px solid'}
        borderColor={'mono3'}
        borderRadius="full"
      >
        <Flex
          alignItems={'center'}
          gap={2}
        >
          {!showDatesOrOccupancies && <MapMarker size={18} />}
          <TextV2
            typographyVariant={'label2'}
            fontWeight={400}
            maxWidth={pxToRem(150)}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {state.searchLocation}
          </TextV2>
        </Flex>
        {showDatesOrOccupancies && (
          <Flex
            alignItems={'center'}
            marginTop={2}
            marginRight={4}
            gap={4}
          >
            {searchForm.dateRangeEnabled && (
              <>
                <TextV2
                  typographyVariant={'label1'}
                  color={'mono10'}
                >
                  {checkinDate} — {checkoutDate}
                </TextV2>
                {searchForm.occupancySelectorEnabled && (
                  <Divider
                    orientation="vertical"
                    height={14}
                  />
                )}
              </>
            )}
            {searchForm.occupancySelectorEnabled && (
              <TextV2
                typographyVariant={'label1'}
                color={'mono10'}
              >
                {headerTrans.rooms.rooms({ rooms: state.occupancies.unitCount, count: state.occupancies.unitCount })},{' '}
                {headerTrans.guests.guests({ guests: adults + children, count: adults + children })}
              </TextV2>
            )}
          </Flex>
        )}
      </Flex>
      <Filter
        size={22}
        cursor={'pointer'}
        onClick={onFilterPanelOpen}
      />
    </Flex>
  );
};

const DeskTopHeader = ({
  handleFormSubmit,
  searchFormInitialValue,
  onClose,
}: {
  handleFormSubmit: (value: StaysSearchTerms) => void;
  searchFormInitialValue: Partial<StaysSearchTerms>;
  onClose?: () => void;
}) => {
  const {
    topBar: { backButton },
  } = useAccommodationsConsoleConfig();
  return (
    <Box flex={1}>
      {backButton && (
        <BackButton
          label={backButton.label}
          onClose={onClose}
        />
      )}
      <LocationDatesOccupants
        onSubmit={handleFormSubmit}
        initialValue={searchFormInitialValue}
      />
      <CloseButton
        overrides={{
          Root: { props: { ...closeButtonOverrides.Root?.props, position: 'absolute', top: pxToRem(20), right: pxToRem(20) } },
          Icon: { props: { ...closeButtonOverrides.Icon?.props } },
        }}
        onClick={onClose}
        aria-label={'close dialog'}
      />
    </Box>
  );
};

interface BackButtonProps {
  label: Maybe<string>;
  onClose?: () => void;
}

const BackButton: React.FC<BackButtonProps> = ({ label, onClose }) => {
  return (
    <Flex
      position={'absolute'}
      left={pxToRem(24)}
      top={pxToRem(24)}
      alignItems={'center'}
    >
      <ButtonV2
        variant="outline"
        border={'1px solid'}
        borderColor={'mono3'}
        height={8}
        minWidth={'fit-content'}
        minHeight={'fit-content'}
        padding={pxToRem(7)}
        intent="neutral"
        marginRight={4}
        width={8}
        data-testid="back-button"
        shape="rounded"
        startIcon={<ChevronBack size={16} />}
        onClick={onClose}
      />
      {label && <TextV2 typographyVariant={'hed4'}>{label}</TextV2>}
    </Flex>
  );
};

AccommodationsConsoleHeader.Skeleton = () => {
  return <Container />;
};

export { AccommodationsConsoleHeader };
